import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Calendar, MapPin, Search, LogIn, Loader2, 
  ArrowRight, Phone, Mail, Building2, User,
  CheckCircle, ChevronRight
} from 'lucide-react';
import PhoneInput, { validatePhone } from '../components/ui/PhoneInput';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Uploaded logos are stored as relative "/api/uploads/..." paths served by the
// backend. The frontend can run on a different origin, so these must be made
// absolute (against the backend) or the <img> resolves to the frontend origin
// and 404s. Mirrors the helper used across Campaigns/FormBuilder/etc.
const toAbsUrl = (url) => url?.startsWith('/api/uploads/') ? `${API_BASE_URL}${url}` : (url || '');

const PublicEventsHome = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    contact_name: '',
    mobile_number: '',
    email: '',
    club_name: '',
    district: '',
    designation: '',
    purpose: '',
    estimated_registrants: '',
    event_date: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/public/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.organization_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const formatEventDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(parseISO(dateString), 'EEE, dd MMM, h:mm a');
    } catch {
      return '';
    }
  };

  const placeholderImages = [
    'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=500&fit=crop',
    'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=400&h=500&fit=crop',
    'https://images.unsplash.com/photo-1511578314322-379afb476865?w=400&h=500&fit=crop',
    'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&h=500&fit=crop',
  ];

  const getEventImage = (event, index) => {
    const logo = event.logo_url || event.event_logo;
    if (logo) return toAbsUrl(logo);
    return placeholderImages[index % placeholderImages.length];
  };

  const handleContactFormChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!validatePhone(contactForm.mobile_number, contactForm._countryCode || 'IN')) {
      alert('Please enter a valid mobile number');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/contact-sales`, contactForm);
      setSubmitSuccess(true);
      setTimeout(() => {
        setShowContactModal(false);
        setSubmitSuccess(false);
        setContactForm({
          contact_name: '',
          mobile_number: '',
          email: '',
          club_name: '',
          district: '',
          designation: '',
          purpose: '',
          estimated_registrants: '',
          event_date: ''
        });
      }, 2000);
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">K</span>
              </div>
              <span className="text-xl font-bold text-gray-900">Kaizen Events</span>
            </Link>

            {/* Search Bar */}
            <div className="hidden md:flex flex-1 max-w-xl mx-8">
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search for events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 h-11 bg-gray-50 border-gray-200 rounded-full text-sm focus:bg-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => setShowContactModal(true)}
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Host an Event
              </Button>
              <Link to="/login">
                <Button className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-5">
                  <LogIn className="w-4 h-4 mr-2" />
                  Login
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Search */}
        <div className="md:hidden px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search for events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 h-11 bg-gray-50 border-gray-200 rounded-full text-sm"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Section Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Live Events
          </h1>
          <p className="text-gray-500">
            Discover and register for upcoming events
          </p>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Events Found</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {searchQuery 
                ? 'Try adjusting your search to find events' 
                : 'Check back soon for upcoming events'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            {filteredEvents.map((event, index) => (
              <Link 
                key={event.id} 
                to={event.has_landing_page ? `/event/${event.slug}` : `/register/${event.slug}`}
                className="group"
              >
                <div className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300">
                  {/* Event Image */}
                  <div className="relative aspect-[3/4] overflow-hidden rounded-xl">
                    <img
                      src={getEventImage(event, index)}
                      alt={event.name}
                      loading="lazy"
                      onError={(e) => {
                        // A broken/missing logo would otherwise show the alt text or a
                        // broken-image icon. Fall back to a placeholder once (guarded
                        // against an infinite loop if the placeholder itself fails).
                        if (e.currentTarget.dataset.fallback) return;
                        e.currentTarget.dataset.fallback = '1';
                        e.currentTarget.src = placeholderImages[index % placeholderImages.length];
                      }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Date Badge */}
                    {event.start_date && (
                      <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-2.5 py-1.5 shadow-sm">
                        <div className="text-[10px] font-bold text-violet-600 uppercase tracking-wide">
                          {format(parseISO(event.start_date), 'MMM')}
                        </div>
                        <div className="text-lg font-bold text-gray-900 leading-none">
                          {format(parseISO(event.start_date), 'dd')}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Event Info */}
                  <div className="pt-3 pb-1">
                    {event.start_date && (
                      <p className="text-xs text-gray-500 mb-1">
                        {formatEventDate(event.start_date)}
                      </p>
                    )}
                    <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-1 line-clamp-2 group-hover:text-violet-600 transition-colors">
                      {event.name}
                    </h3>
                    <p className="text-xs text-gray-500 line-clamp-1">
                      {event.organization_name}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Results Count */}
        {!loading && filteredEvents.length > 0 && (
          <div className="mt-8 text-center text-sm text-gray-500">
            Showing {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">K</span>
                </div>
                <span className="text-xl font-bold text-gray-900">Kaizen Events</span>
              </div>
              <p className="text-gray-500 text-sm max-w-sm mb-4">
                The complete event management platform for organizations. Create, manage, and grow your events.
              </p>
              <Button 
                onClick={() => setShowContactModal(true)}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
              >
                Host Your Event
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-gray-500 hover:text-violet-600">Browse Events</Link></li>
                <li><Link to="/login" className="text-gray-500 hover:text-violet-600">Admin Login</Link></li>
                <li><Link to="/contact-sales" className="text-gray-500 hover:text-violet-600">Contact Sales</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy-policy" className="text-gray-500 hover:text-violet-600">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="text-gray-500 hover:text-violet-600">Terms of Service</Link></li>
                <li><Link to="/refund-policy" className="text-gray-500 hover:text-violet-600">Refund Policy</Link></li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-200 mt-8 pt-8 text-center text-sm text-gray-500">
            © {new Date().getFullYear()} Kaizen Infotech Solutions Pvt. Ltd. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Contact Sales Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-[600px] bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Phone className="w-5 h-5 text-violet-600" />
              Host Your Event
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Fill out the form below and our team will get back to you within 24 hours.
            </DialogDescription>
          </DialogHeader>
          
          {submitSuccess ? (
            <div className="py-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h3>
              <p className="text-gray-500">Our team will contact you shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_name" className="text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Contact Person *
                  </Label>
                  <Input
                    id="contact_name"
                    value={contactForm.contact_name}
                    onChange={(e) => handleContactFormChange('contact_name', e.target.value)}
                    placeholder="Enter your name"
                    required
                    className="border-gray-200"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="mobile_number" className="text-gray-700 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Mobile Number *
                  </Label>
                  <PhoneInput
                    value={contactForm.mobile_number}
                    onChange={(v) => handleContactFormChange('mobile_number', v)}
                    onCountryChange={(c) => handleContactFormChange('_countryCode', c)}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email ID *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => handleContactFormChange('email', e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="border-gray-200"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="club_name" className="text-gray-700 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Club / Organization *
                  </Label>
                  <Input
                    id="club_name"
                    value={contactForm.club_name}
                    onChange={(e) => handleContactFormChange('club_name', e.target.value)}
                    placeholder="Enter club name"
                    required
                    className="border-gray-200"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="district" className="text-gray-700">
                    District *
                  </Label>
                  <Input
                    id="district"
                    value={contactForm.district}
                    onChange={(e) => handleContactFormChange('district', e.target.value)}
                    placeholder="Enter district"
                    required
                    className="border-gray-200"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="designation" className="text-gray-700">
                  Designation *
                </Label>
                <Input
                  id="designation"
                  value={contactForm.designation}
                  onChange={(e) => handleContactFormChange('designation', e.target.value)}
                  placeholder="e.g., President, Secretary"
                  required
                  className="border-gray-200"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="purpose" className="text-gray-700">
                  Event Purpose *
                </Label>
                <Textarea
                  id="purpose"
                  value={contactForm.purpose}
                  onChange={(e) => handleContactFormChange('purpose', e.target.value)}
                  placeholder="Describe your event briefly..."
                  required
                  rows={3}
                  className="border-gray-200 resize-none"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="estimated_registrants" className="text-gray-700">
                    Expected Attendees *
                  </Label>
                  <Select
                    value={contactForm.estimated_registrants}
                    onValueChange={(value) => handleContactFormChange('estimated_registrants', value)}
                  >
                    <SelectTrigger className="border-gray-200">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0-100">0 - 100</SelectItem>
                      <SelectItem value="100-500">100 - 500</SelectItem>
                      <SelectItem value="500-1000">500 - 1,000</SelectItem>
                      <SelectItem value="1000+">1,000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="event_date" className="text-gray-700 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Event Date *
                  </Label>
                  <Input
                    id="event_date"
                    type="date"
                    value={contactForm.event_date}
                    onChange={(e) => handleContactFormChange('event_date', e.target.value)}
                    required
                    className="border-gray-200"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowContactModal(false)}
                  className="flex-1 border-gray-200"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-violet-600 hover:bg-violet-700"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicEventsHome;
